import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useServices } from '../../../../core/contexts/useServices';
import { API_CLIENTS_QUERY_KEY } from './useApiClients';

export const API_CLIENT_DETAIL_QUERY_KEY = (id: string) => ['api-client', id] as const;

export function useApiClientDetail(id: string) {
  const { apiClientService } = useServices();
  return useQuery({
    queryKey: API_CLIENT_DETAIL_QUERY_KEY(id),
    queryFn: () => apiClientService.getOne(id),
    enabled: !!id,
  });
}

export function useRevokeSecret(apiClientId: string) {
  const { apiClientService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (secretId: string) => apiClientService.revokeSecret(apiClientId, secretId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_CLIENT_DETAIL_QUERY_KEY(apiClientId) });
    },
  });
}

export function useRotateSecret(apiClientId: string) {
  const { apiClientService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (secretId: string) => apiClientService.rotateSecret(apiClientId, secretId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_CLIENT_DETAIL_QUERY_KEY(apiClientId) });
    },
  });
}

export function useDeleteApiClient() {
  const { apiClientService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClientService.deleteClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_CLIENTS_QUERY_KEY });
    },
  });
}

export function useRevokeApiClient() {
  const { apiClientService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClientService.revokeClient(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: API_CLIENT_DETAIL_QUERY_KEY(id) });
      queryClient.invalidateQueries({ queryKey: API_CLIENTS_QUERY_KEY });
    },
  });
}

export function useSuspendApiClient() {
  const { apiClientService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClientService.suspendClient(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: API_CLIENT_DETAIL_QUERY_KEY(id) });
      queryClient.invalidateQueries({ queryKey: API_CLIENTS_QUERY_KEY });
    },
  });
}

export function useActivateApiClient() {
  const { apiClientService } = useServices();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClientService.activateClient(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: API_CLIENT_DETAIL_QUERY_KEY(id) });
      queryClient.invalidateQueries({ queryKey: API_CLIENTS_QUERY_KEY });
    },
  });
}
